import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { AuthSession, Membership, Profile, User } from '@/lib/api/types';

/**
 * Login state. Persisted in localStorage ("cf.session") so a reload keeps you signed in.
 * The JWT has no refresh flow in API v0.2; a 401 clears the session and sends the user to /login.
 */
export interface SessionState {
  token: string | null;
  user: User | null;
  memberships: Membership[];
  workspaceId: string | null;
  setSession: (s: AuthSession) => void;
  setProfile: (p: Profile) => void;
  setWorkspace: (id: string) => void;
  logout: () => void;
}

const pickWorkspace = (memberships: Membership[], current: string | null) =>
  memberships.some((m) => m.workspaceId === current) ? current : (memberships[0]?.workspaceId ?? null);

export const useSession = create<SessionState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      memberships: [],
      workspaceId: null,
      setSession: ({ token, user, memberships }) => set({ token, user, memberships, workspaceId: pickWorkspace(memberships, get().workspaceId) }),
      setProfile: ({ user, memberships }) => set({ user, memberships, workspaceId: pickWorkspace(memberships, get().workspaceId) }),
      setWorkspace: (id) => set({ workspaceId: id }),
      logout: () => set({ token: null, user: null, memberships: [], workspaceId: null }),
    }),
    { name: 'cf.session', storage: createJSONStorage(() => localStorage), version: 1 },
  ),
);

/** The membership for the active workspace (role, external flag). */
export function useMembership(): Membership | null {
  return useSession((s) => s.memberships.find((m) => m.workspaceId === s.workspaceId) ?? null);
}
